(() => {
  const CONFIG_ID = "WcCartFreeGiftConfig";
  const GIFT_PROPERTY = "_wc_free_gift";
  const EVENT_SOURCE = "wc-free-gift";
  const DISMISSAL_STORAGE_PREFIX = "wc-cart-free-gift-dismissed-v2";
  const LEGACY_DISMISSAL_STORAGE_PREFIX = "wc-cart-free-gift-dismissed";
  const FETCH_OBSERVER_KEY = "__wcCartFreeGiftFetchObserver";
  const OWN_REQUEST_HEADER = "X-Wc-Free-Gift";

  function readConfig() {
    const element = document.getElementById(CONFIG_ID);
    if (!element) return null;

    try {
      return JSON.parse(element.textContent || "{}");
    } catch (error) {
      console.error("[Wooky cadeau] Configuration invalide.", error);
      return null;
    }
  }

  function getRequestUrl(resource) {
    if (typeof resource === "string") return resource;
    if (resource instanceof URL) return resource.toString();
    if (typeof Request !== "undefined" && resource instanceof Request) {
      return resource.url;
    }
    return "";
  }

  function getRequestMethod(resource, init) {
    const initMethod = init && init.method;
    if (initMethod) return String(initMethod).toUpperCase();

    if (typeof Request !== "undefined" && resource instanceof Request) {
      return String(resource.method || "GET").toUpperCase();
    }

    return "GET";
  }

  function getHeaderValue(resource, init, headerName) {
    const normalizedName = String(headerName).toLowerCase();
    const candidates = [];

    if (init && init.headers) candidates.push(init.headers);
    if (typeof Request !== "undefined" && resource instanceof Request) {
      candidates.push(resource.headers);
    }

    for (const headers of candidates) {
      if (typeof Headers !== "undefined" && headers instanceof Headers) {
        const value = headers.get(headerName);
        if (value !== null) return value;
        continue;
      }

      if (Array.isArray(headers)) {
        const entry = headers.find(
          ([name]) => String(name).toLowerCase() === normalizedName
        );
        if (entry) return String(entry[1]);
        continue;
      }

      if (headers && typeof headers === "object") {
        const key = Object.keys(headers).find(
          (name) => String(name).toLowerCase() === normalizedName
        );
        if (key) return String(headers[key]);
      }
    }

    return "";
  }

  function isCartMutationRequest(resource, init) {
    if (getRequestMethod(resource, init) !== "POST") return false;

    const rawUrl = getRequestUrl(resource);
    if (!rawUrl) return false;

    try {
      const pathname = new URL(rawUrl, window.location.origin).pathname;
      return /(?:^|\/)cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(
        pathname
      );
    } catch (error) {
      return false;
    }
  }

  function installCartFetchObserver(listener) {
    if (typeof window.fetch !== "function") return () => {};

    if (!window[FETCH_OBSERVER_KEY]) {
      const listeners = new Set();
      const originalFetch = window.fetch.bind(window);

      window.fetch = async (...args) => {
        const resource = args[0];
        const init = args[1] || {};
        const shouldNotify = isCartMutationRequest(resource, init);
        const isOwnRequest =
          getHeaderValue(resource, init, OWN_REQUEST_HEADER) === "true";
        const response = await originalFetch(...args);

        if (response.ok && shouldNotify && !isOwnRequest) {
          window.setTimeout(() => {
            listeners.forEach((registeredListener) => registeredListener());
          }, 0);
        }

        return response;
      };

      window[FETCH_OBSERVER_KEY] = { listeners };
    }

    const observer = window[FETCH_OBSERVER_KEY];
    observer.listeners.add(listener);

    return () => observer.listeners.delete(listener);
  }

  class WcCartFreeGiftManager {
    constructor(config) {
      this.variantId = String(config.variantId || "");
      this.useItemsSubtotal = config.useItemsSubtotal === true;
      this.rules = {
        goal3: {
          enabled: config.goal3?.enabled === true,
          thresholdCents: Number(config.goal3?.thresholdCents || 0),
        },
        subscription: {
          enabled: config.subscription?.enabled === true,
          thresholdCents: Number(config.subscription?.thresholdCents || 0),
        },
      };
      this.syncInProgress = false;
      this.syncQueued = false;
      this.syncTimer = null;
      this.retryCount = 0;
      this.lastCartToken = "";
      this.lastAutoGiftPresent = false;
      this.removeFetchObserver = null;
    }

    init() {
      if (!this.variantId || !this.hasValidRule()) return;

      this.cleanupLegacyDismissals();
      this.removeFetchObserver = installCartFetchObserver(() => {
        this.scheduleSync(120);
      });

      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === EVENT_SOURCE) return;
          this.handleExternalCart(event?.cartData);
        });
      }

      document.addEventListener("cart:open", () => this.scheduleSync(0));
      document.addEventListener("cart:updated", (event) => {
        if (event?.detail?.source === EVENT_SOURCE) return;
        this.handleExternalCart(event?.detail?.cart);
      });
      document.addEventListener("shopify:section:load", () => {
        this.scheduleSync(80);
      });
      window.addEventListener("pageshow", () => this.scheduleSync(0));

      this.scheduleSync(0);
    }

    hasValidRule() {
      return Object.values(this.rules).some(
        (rule) =>
          rule.enabled &&
          Number.isFinite(rule.thresholdCents) &&
          rule.thresholdCents > 0
      );
    }

    handleExternalCart(cart) {
      if (this.isFullCart(cart)) {
        this.captureCartState(cart, true);
      }
      this.scheduleSync(100);
    }

    scheduleSync(delay = 80) {
      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => {
        this.sync()
          .then(() => {
            this.retryCount = 0;
          })
          .catch((error) => {
            console.error("[Wooky cadeau] Synchronisation impossible.", error);

            if (this.retryCount < 2) {
              this.retryCount += 1;
              this.scheduleSync(350 * this.retryCount);
            }
          });
      }, delay);
    }

    async sync() {
      if (this.syncInProgress) {
        this.syncQueued = true;
        return;
      }

      this.syncInProgress = true;

      try {
        do {
          this.syncQueued = false;
          await this.syncOnce();
        } while (this.syncQueued);
      } finally {
        this.syncInProgress = false;
      }
    }

    async syncOnce() {
  let cart = await this.fetchCart();
  this.captureCartState(cart, true);

  let sections = {};
  let cartMutated = false;
  const normalization = this.getGiftLineNormalization(cart);

  if (Object.keys(normalization.updates).length > 0) {
    const response = await this.updateGiftLines(normalization.updates);
    sections = { ...sections, ...(response.sections || {}) };
    cartMutated = true;
    cart = await this.fetchCart();
    this.captureCartState(cart, false);
  }

  const matchingGift = this.getMatchingGift(cart);
  const giftVariantPresent = this.hasGiftVariant(cart);
  const qualification = this.getQualification(cart);

  if (!qualification.qualifies) {
    this.setGiftDismissed(cart, false);

    if (matchingGift?.key) {
      const response = await this.updateGiftLines({
        [matchingGift.key]: 0,
      });

      sections = { ...sections, ...(response.sections || {}) };
      cartMutated = true;

      cart = await this.fetchCart();
      this.captureCartState(cart, false);
    }
  } else if (
    !matchingGift &&
    !giftVariantPresent &&
    !this.isGiftDismissed(cart)
  ) {
    const response = await this.addGift(qualification.trigger);

    sections = { ...sections, ...(response.sections || {}) };
    cartMutated = true;

    cart = await this.fetchCart();

    this.setGiftDismissed(cart, false);
    this.captureCartState(cart, false);
  }

  if (cartMutated) {
    this.refreshSections(sections);
    this.updateProgressBar(cart);
    this.publishCartUpdate(cart);
  } else {
    this.updateProgressBar(cart);
  }
}




    
    getGiftItems(cart) {
      if (!cart || !Array.isArray(cart.items)) return [];

      return cart.items.filter(
        (item) =>
          item &&
          item.properties &&
          String(item.properties[GIFT_PROPERTY]) === "true"
      );
    }

    getMatchingGift(cart) {
      return this.getGiftItems(cart).find(
        (item) => String(item.variant_id) === this.variantId
      );
    }

    getGiftLineNormalization(cart) {
      const updates = {};
      let selectedGiftKey = "";

      this.getGiftItems(cart).forEach((item) => {
        const isSelectedVariant = String(item.variant_id) === this.variantId;
        const keepLine = isSelectedVariant && !selectedGiftKey;
        const wantedQuantity = keepLine ? 1 : 0;

        if (keepLine) selectedGiftKey = String(item.key || "");
        if (Number(item.quantity) !== wantedQuantity) {
          updates[item.key] = wantedQuantity;
        }
      });

      return { updates, selectedGiftKey };
    }

    hasGiftVariant(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;

      return cart.items.some(
        (item) => item && String(item.variant_id) === this.variantId
      );
    }

    getNonGiftItems(cart) {
      if (!cart || !Array.isArray(cart.items)) return [];

      return cart.items.filter(
        (item) =>
          !item?.properties ||
          String(item.properties[GIFT_PROPERTY]) !== "true"
      );
    }

    getQualifyingTotal(cart) {
      const baseTotal = Number(
        this.useItemsSubtotal ? cart.items_subtotal_price : cart.total_price
      );

      const giftTotal = this.getGiftItems(cart).reduce(
        (total, item) => total + Number(item.final_line_price || 0),
        0
      );

      return Math.max(0, baseTotal - giftTotal);
    }

    hasSubscription(cart) {
      return Boolean(
        cart &&
          Array.isArray(cart.items) &&
          cart.items.some(
            (item) =>
              Boolean(item?.selling_plan_allocation) &&
              String(item.variant_id) !== this.variantId
          )
      );
    }

    getQualification(cart) {
      const totalCents = this.getQualifyingTotal(cart);
      const subscriptionQualifies =
        this.rules.subscription.enabled &&
        this.hasSubscription(cart) &&
        totalCents >= this.rules.subscription.thresholdCents;
      const goal3Qualifies =
        this.rules.goal3.enabled &&
        totalCents >= this.rules.goal3.thresholdCents;

      return {
        qualifies: subscriptionQualifies || goal3Qualifies,
        trigger: subscriptionQualifies ? "subscription" : "goal_3",
      };
    }

    getRootUrl() {
      return window.Shopify?.routes?.root || "/";
    }

    getSectionsUrl() {
      return window.routes?.cart_url || `${this.getRootUrl()}cart`;
    }

    getRequestedSections() {
      const sections = ["cart-drawer", "cart-icon-bubble"];

      if (document.getElementById("main-cart-items")) {
        sections.push("main-cart-items");
      }

      if (document.getElementById("main-cart-footer")) {
        sections.push("main-cart-footer");
      }

      return sections;
    }

    getCartToken(cart) {
      return String(cart?.token || this.lastCartToken || "cart-session");
    }

    getDismissalStorageKey(cart) {
      return `${DISMISSAL_STORAGE_PREFIX}:${this.variantId}:${this.getCartToken(cart)}`;
    }

    getStorage() {
      try {
        return window.localStorage || null;
      } catch (error) {
        return null;
      }
    }

    cleanupLegacyDismissals() {
      const storage = this.getStorage();
      if (!storage) return;

      const prefix = `${LEGACY_DISMISSAL_STORAGE_PREFIX}:${this.variantId}:`;

      try {
        const keysToRemove = [];
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key && key.startsWith(prefix)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => storage.removeItem(key));
      } catch (error) {
        return;
      }
    }

    isGiftDismissed(cart) {
      const storage = this.getStorage();
      if (!storage) return false;

      try {
        return storage.getItem(this.getDismissalStorageKey(cart)) === "true";
      } catch (error) {
        return false;
      }
    }

    setGiftDismissed(cart, dismissed) {
      const storage = this.getStorage();
      if (!storage) return;

      try {
        const key = this.getDismissalStorageKey(cart);
        if (dismissed) {
          storage.setItem(key, "true");
        } else {
          storage.removeItem(key);
        }
      } catch (error) {
        return;
      }
    }

    isCartEmpty(cart) {
      if (!cart || !Array.isArray(cart.items)) return true;

      return (
        cart.items.reduce(
          (total, item) => total + Number(item?.quantity || 0),
          0
        ) === 0
      );
    }

    captureCartState(cart, detectManualRemoval) {
      if (!this.isFullCart(cart)) return;

      const cartToken = this.getCartToken(cart);
      const autoGiftPresent = Boolean(this.getMatchingGift(cart));
      const selectedVariantPresent = this.hasGiftVariant(cart);
      const hasOtherItems = this.getNonGiftItems(cart).length > 0;
      const sameCart = !this.lastCartToken || this.lastCartToken === cartToken;

      if (
        detectManualRemoval &&
        sameCart &&
        this.lastAutoGiftPresent &&
        !autoGiftPresent &&
        !selectedVariantPresent &&
        hasOtherItems
      ) {
        this.setGiftDismissed(cart, true);
      }

      if (this.isCartEmpty(cart)) {
        this.setGiftDismissed(cart, false);
      }

      this.lastCartToken = cartToken;
      this.lastAutoGiftPresent = autoGiftPresent;
    }

    async fetchCart() {
      const response = await fetch(`${this.getRootUrl()}cart.js`, {
        headers: {
          Accept: "application/json",
          [OWN_REQUEST_HEADER]: "true",
        },
        credentials: "same-origin",
      });

      return this.parseResponse(response);
    }

    async addGift(trigger) {
      return this.postCart("cart/add.js", {
        items: [
          {
            id: Number(this.variantId),
            quantity: 1,
            properties: {
              [GIFT_PROPERTY]: "true",
              _wc_free_gift_trigger: trigger,
            },
          },
        ],
        sections: this.getRequestedSections(),
        sections_url: this.getSectionsUrl(),
      });
    }

    async updateGiftLines(updates) {
      return this.postCart("cart/update.js", {
        updates,
        sections: this.getRequestedSections(),
        sections_url: this.getSectionsUrl(),
      });
    }

    async postCart(endpoint, body) {
      const response = await fetch(`${this.getRootUrl()}${endpoint}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          [OWN_REQUEST_HEADER]: "true",
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      });

      return this.parseResponse(response);
    }

    async parseResponse(response) {
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        const message =
          data.description || data.message || `Erreur panier ${response.status}`;
        throw new Error(message);
      }

      return data;
    }

    isFullCart(data) {
      return (
        data &&
        Array.isArray(data.items) &&
        Number.isFinite(Number(data.total_price))
      );
    }

    refreshSections(sections) {
      if (!sections || typeof sections !== "object") return;

      this.refreshCartIcon(sections["cart-icon-bubble"]);
      this.refreshCartDrawer(sections["cart-drawer"]);
      this.refreshThemeSection(sections["main-cart-items"]);
      this.refreshThemeSection(sections["main-cart-footer"]);
    }

    parseHtml(html) {
      if (!html) return null;
      return new DOMParser().parseFromString(html, "text/html");
    }

    refreshCartIcon(html) {
      const target = document.getElementById("cart-icon-bubble");
      const documentFragment = this.parseHtml(html);
      const source = documentFragment?.querySelector(".shopify-section");

      if (target && source) {
        target.innerHTML = source.innerHTML;
      }
    }

    refreshCartDrawer(html) {
      const targetDrawer = document.querySelector("cart-drawer");
      const target = document.getElementById("CartDrawer");
      const documentFragment = this.parseHtml(html);
      const sourceDrawer = documentFragment?.querySelector("cart-drawer");
      const source = documentFragment?.getElementById("CartDrawer");

      if (!target || !source) return;

      target.innerHTML = source.innerHTML;

      if (targetDrawer) {
        const isEmpty = sourceDrawer
          ? sourceDrawer.classList.contains("is-empty")
          : Boolean(source.querySelector(".drawer__inner-empty"));
        targetDrawer.classList.toggle("is-empty", isEmpty);
      }

      const drawerItems = target.querySelector("cart-drawer-items");
      if (drawerItems) {
        drawerItems.classList.toggle(
          "is-empty",
          Boolean(targetDrawer?.classList.contains("is-empty"))
        );
      }

      const overlay = target.querySelector("#CartDrawer-Overlay");
      if (targetDrawer && overlay && typeof targetDrawer.close === "function") {
        overlay.addEventListener("click", targetDrawer.close.bind(targetDrawer));
      }
    }

    refreshThemeSection(html) {
      const documentFragment = this.parseHtml(html);
      const source = documentFragment?.querySelector(".shopify-section");

      if (!source || !source.id) return;

      const target = document.getElementById(source.id);
      if (target) target.innerHTML = source.innerHTML;
    }

    updateProgressBar(cart) {
      const cartItems =
        document.querySelector("cart-items") ||
        document.querySelector("cart-drawer-items");

      if (cartItems && typeof cartItems.updateProgressBar === "function") {
        cartItems.updateProgressBar({
          total_price: cart.total_price,
          items_subtotal_price: cart.items_subtotal_price,
          items: cart.items,
        });
      }
    }

    publishCartUpdate(cart) {
      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: EVENT_SOURCE,
          cartData: cart,
          variantId: this.variantId,
        });
      }

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { source: EVENT_SOURCE, cart },
        })
      );
    }
  }

  const config = readConfig();
  if (config) new WcCartFreeGiftManager(config).init();
})();
